import { Button, DataTable, Form, Modal, ResultsCard, ResultsToolbar, RowActions } from 'shared/components/ui';
import React, { useState, useEffect } from 'react';
import { Palette, Add, Save } from '@mui/icons-material';

import Header from '../../../shared/components/layout/Header';
import Footer from '../../../shared/components/layout/Footer';
import ConfirmModal from '../../../shared/components/ConfirmModal';
import { Toast } from '../../../shared/components/Toast';
import { useColoresEstudios } from '../hooks/useColoresEstudios';
import '../../../features/turnos/components/GrillaTurnos.css';
import '../../../styles/buttons.css';

const FORM_INICIAL = { idTipoEstudio: '', idCentro: '', color: '#000000' };

const GrillaColoresEstudios = () => {
  const { colores, tiposEstudios, centros, loading, cargarFiltros, buscar, guardar, actualizar, eliminar } = useColoresEstudios();
  const [modal, setModal] = useState(null);
  const [form, setForm] = useState(FORM_INICIAL);
  const [confirmEliminar, setConfirmEliminar] = useState(null);
  const [toast, setToast] = useState(null);

  useEffect(() => {
    cargarFiltros();
    buscar();
  }, []); // eslint-disable-line

  const esDuplicado = (idTipoEstudio, idCentro, excludeId) =>
    colores.some(c =>
      c.idTipoEstudio === parseInt(idTipoEstudio) &&
      c.idCentro === parseInt(idCentro) &&
      c.idColor !== excludeId
    );

  const abrirNuevo = () => { setForm(FORM_INICIAL); setModal({ mode: 'nuevo' }); };
  const abrirEditar = (item) => {
    setForm({ idTipoEstudio: String(item.idTipoEstudio), idCentro: String(item.idCentro), color: item.color });
    setModal({ mode: 'editar', item });
  };

  const handleGuardar = async (e) => {
    e.preventDefault();
    const excludeId = modal.mode === 'editar' ? modal.item.idColor : undefined;
    if (esDuplicado(form.idTipoEstudio, form.idCentro, excludeId)) {
      setToast({ message: 'Ya existe un color para esta combinación de práctica y centro.', type: 'error' });
      return;
    }
    const body = { IdTipoEstudio: parseInt(form.idTipoEstudio), IdCentro: parseInt(form.idCentro), Color: form.color };
    try {
      if (modal.mode === 'editar') {
        await actualizar(modal.item.idColor, body);
        setToast({ message: 'Color actualizado con éxito.', type: 'success' });
      } else {
        await guardar(body);
        setToast({ message: 'Color guardado con éxito.', type: 'success' });
      }
      setModal(null);
      buscar();
    } catch (err) {
      const msg = err?.response?.status === 409
        ? 'Ya existe un color para esta combinación de práctica y centro.'
        : 'Error al guardar.';
      setToast({ message: msg, type: 'error' });
    }
  };

  const confirmarEliminar = async () => {
    try {
      await eliminar(confirmEliminar.id);
      setToast({ message: 'Color eliminado con éxito.', type: 'success' });
      buscar();
    } catch {
      setToast({ message: 'Error al eliminar.', type: 'error' });
    } finally {
      setConfirmEliminar(null);
    }
  };

  const isDuplicadoActual = !!(form.idTipoEstudio && form.idCentro &&
    esDuplicado(form.idTipoEstudio, form.idCentro, modal?.mode === 'editar' ? modal.item.idColor : undefined));

  return (
    <div className="grilla-turnos">
      <Header />
      <div className="main-content">
        <div className="page-header">
          <h2 className="page-title-grilla">
            <Palette sx={{ fontSize: 32, mr: 1, verticalAlign: 'middle', color: '#6d1212' }} />
            Colores de Tipos de Estudios
          </h2>
          <span className="page-subtitle">Asignación de colores por tipo de práctica y centro</span>
        </div>

        <Modal show={modal !== null} onHide={() => setModal(null)} size="lg">
          <Modal.Header closeButton>
            <Modal.Title>{modal?.mode === 'editar' ? 'Editar Color' : 'Nuevo Color'}</Modal.Title>
          </Modal.Header>
          <Form onSubmit={handleGuardar}>
            <Modal.Body style={{ padding: '1.75rem' }}>
              <div className="row g-3">
                <div className="col-12">
                  <Form.Group>
                    <Form.Label>Tipo de Práctica *</Form.Label>
                    <Form.Select value={form.idTipoEstudio} onChange={(e) => setForm(f => ({ ...f, idTipoEstudio: e.target.value }))} required>
                      <option value="">Seleccione...</option>
                      {tiposEstudios.map(t => <option key={t.idTipoEstudio} value={t.idTipoEstudio}>{t.tipoEstudio}</option>)}
                    </Form.Select>
                  </Form.Group>
                </div>
                <div className="col-12">
                  <Form.Group>
                    <Form.Label>Centro *</Form.Label>
                    <Form.Select
                      value={form.idCentro}
                      onChange={(e) => setForm(f => ({ ...f, idCentro: e.target.value }))}
                      required
                      isInvalid={isDuplicadoActual}
                    >
                      <option value="">Seleccione...</option>
                      {centros.map(c => <option key={c.idCentro} value={c.idCentro}>{c.centro1}</option>)}
                    </Form.Select>
                    <Form.Control.Feedback type="invalid">
                      Ya existe un color para esta combinación de práctica y centro.
                    </Form.Control.Feedback>
                  </Form.Group>
                </div>
                <div className="col-md-4">
                  <Form.Group>
                    <Form.Label>Color *</Form.Label>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                      <Form.Control
                        type="color"
                        value={form.color}
                        onChange={(e) => setForm(f => ({ ...f, color: e.target.value }))}
                        style={{ width: '60px', height: '38px', padding: '2px', cursor: 'pointer' }}
                        required
                      />
                      <span style={{ fontFamily: 'monospace', fontSize: '0.9rem' }}>{form.color}</span>
                      <div style={{ width: '32px', height: '32px', borderRadius: '4px', backgroundColor: form.color, border: '1px solid #ccc' }} />
                    </div>
                  </Form.Group>
                </div>
              </div>
            </Modal.Body>
            <Modal.Footer>
              <Button variant="secondary" onClick={() => setModal(null)} disabled={loading}>Cancelar</Button>
              <Button type="submit" variant="success" disabled={loading || !form.idTipoEstudio || !form.idCentro || isDuplicadoActual}>
                <Save sx={{ fontSize: 16, mr: 0.5 }} /> {modal?.mode === 'editar' ? 'Actualizar' : 'Guardar'}
              </Button>
            </Modal.Footer>
          </Form>
        </Modal>

        <ResultsCard className="results-card">
          <ResultsToolbar className="results-header">
            <div className="results-header-left"><h3 className="results-title">Resultados</h3></div>
            <div className="results-info">
              <span className="results-count">{colores.length} registros</span>
              {loading && <span className="loading-indicator">Cargando...</span>}
              <Button variant="success" size="sm" onClick={abrirNuevo} className="btn-new-turno">
                <Add sx={{ fontSize: 18, mr: 0.5 }} /> Nuevo
              </Button>
            </div>
          </ResultsToolbar>
          <div className="table-container table-container-scrollable">
            <DataTable className="data-table">
              <thead>
                <tr>
                  <th>Tipo de Práctica</th>
                  <th>Centro</th>
                  <th style={{ textAlign: 'center' }}>Color</th>
                  <th className="action-cell">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {colores.length === 0 ? (
                  <tr><td colSpan={4} className="empty-state">{loading ? 'Cargando...' : 'No hay registros disponibles'}</td></tr>
                ) : colores.map((c) => (
                  <tr key={c.idColor}>
                    <td>{c.tipoEstudio}</td>
                    <td>{c.centro}</td>
                    <td style={{ textAlign: 'center' }}>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem' }}>
                        <div style={{ width: '24px', height: '24px', borderRadius: '4px', backgroundColor: c.color, border: '1px solid #ccc', display: 'inline-block' }} />
                        <span style={{ fontFamily: 'monospace', fontSize: '0.85rem' }}>{c.color}</span>
                      </div>
                    </td>
                    <td className="action-cell">
                      <RowActions onEdit={() => abrirEditar(c)} onDelete={() => setConfirmEliminar({ id: c.idColor, label: c.tipoEstudio })} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </DataTable>
          </div>
        </ResultsCard>
      </div>
      <Footer />

      <ConfirmModal
        show={confirmEliminar !== null}
        title="Eliminar Color"
        message={`¿Eliminar el color de "${confirmEliminar?.label}"?`}
        onConfirm={confirmarEliminar}
        onCancel={() => setConfirmEliminar(null)}
      />
      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
    </div>
  );
};

export default GrillaColoresEstudios;
